import { useEffect, useState } from 'preact/hooks';

interface ArtworkProps {
  src: string | null | undefined;
  alt: string;
  size: number;
  className?: string;
}

/**
 * Podcast artwork with a graceful fallback.
 *
 * Images come straight from the publisher's CDN — the Worker does not proxy or resize
 * them, so a dead URL is entirely normal and must not leave a broken-image icon.
 */
export function Artwork({ src, alt, size, className }: ArtworkProps) {
  const [failed, setFailed] = useState(false);

  // `failed` latches on a dead URL, but it must not outlive that URL: once the caller
  // hands us a different `src` (e.g. the feed's artwork resolves after an initial
  // render with none, or gets corrected later), a stale `true` from the old image would
  // otherwise pin the placeholder on screen forever even though the new src is fine.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <div
        class={`artwork artwork--placeholder ${className ?? ''}`}
        style={{ width: size, height: size }}
        aria-hidden={alt ? undefined : 'true'}
        role={alt ? 'img' : undefined}
        aria-label={alt || undefined}
      >
        <svg viewBox="0 0 24 24" width={size * 0.4} height={size * 0.4} aria-hidden="true">
          <path
            d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM5 11a7 7 0 0 0 14 0M12 18v3"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <img
      class={`artwork ${className ?? ''}`}
      src={src}
      alt={alt}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}
